import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    ButtonBase,
    CircularProgress,
    MenuItem,
    Select,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { alpha, darken, lighten } from "@mui/material/styles";
import StorefrontRounded from "@mui/icons-material/StorefrontRounded";
import LocationCityRounded from "@mui/icons-material/LocationCityRounded";
import PlaceRounded from "@mui/icons-material/PlaceRounded";
import LanguageRounded from "@mui/icons-material/LanguageRounded";
import AttachMoneyRounded from "@mui/icons-material/AttachMoneyRounded";
import TextField from "../../TextField";
import Location from "../../../api/Location";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import BottomButton from "../../BottomButton";
import { getPreferredLanguage } from "../../../utils/languageUtils";
import InlineExplainer from "../../InlineExplainer";
import Brand from "../../../api/Brand";
import ToggleButton from "../../ToggleButton";
import ColoredIcon from "../../ColoredIcon";
import BackIcon from "../../../img/Arrow_Left.svg";
import { FONTS, CARD_STYLES, FORM_LABEL_SX } from "../../../constants/styles";

const NOISE_TEXTURE = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

type DayCode = "MO" | "TU" | "WE" | "TH" | "FR" | "SA" | "SU";
type DayCfg = { enabled: boolean; start: number; end: number }; // minutes 0-1439
type WeekCfg = Record<DayCode, DayCfg>;

const DAY_ORDER: DayCode[] = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

const DAY_LABELS: Record<DayCode, string> = {
    MO: "Mon", TU: "Tue", WE: "Wed", TH: "Thu", FR: "Fri", SA: "Sat", SU: "Sun",
};

// 30-min time slots for working hours selects
const TIME_SLOTS: number[] = [];
for (let m = 0; m < 24 * 60; m += 30) TIME_SLOTS.push(m);

// Include the stored value if it's not on a 30-min boundary
const ensureSlot = (val: number, slots: number[]) =>
    slots.includes(val) ? slots : [...slots, val].sort((a, b) => a - b);

const toHHMM = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const z = (x: number) => (x < 10 ? "0" : "") + x;
    return `${z(h)}:${z(m)}`;
};

const WorkspaceEdit: React.FC = () => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const navigate = useNavigate();

    const [cookies] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookies;

    const [locationId, setLocationId] = useState<string | undefined>();
    const [location, setLocation] = useState<Location | undefined>();

    const [name, setName] = useState("");
    const [city, setCity] = useState("");
    const [address, setAddress] = useState("");
    const [timeZone, setTimeZone] = useState("Europe/Berlin");
    const [currency, setCurrency] = useState("EUR");
    // Stored as strings for the inputs; empty string = "not set" (saved as null).
    const [monthlyRevenueGoal, setMonthlyRevenueGoal] = useState("");
    const [monthlyHoursGoal, setMonthlyHoursGoal] = useState("");

    const [week, setWeek] = useState<WeekCfg>(() => ({
        MO: { enabled: true, start: 9 * 60, end: 18 * 60 },
        TU: { enabled: true, start: 9 * 60, end: 18 * 60 },
        WE: { enabled: true, start: 9 * 60, end: 18 * 60 },
        TH: { enabled: true, start: 9 * 60, end: 18 * 60 },
        FR: { enabled: true, start: 9 * 60, end: 18 * 60 },
        SA: { enabled: false, start: 9 * 60, end: 18 * 60 },
        SU: { enabled: false, start: 9 * 60, end: 18 * 60 },
    }));

    // Aggregate validation flag
    const [hasValidationError, setHasValidationError] = useState(false);

    const cityInputRef = useRef<HTMLInputElement>(null);
    const addressInputRef = useRef<HTMLInputElement>(null);

    // For initial onboarding
    const [locationConfigured, setLocationConfigured] = useState(true);
    const [showExplainer, setShowExplainer] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Scroll tracking
    const scrollRef = useRef<HTMLDivElement>(null);
    const heroRef = useRef<HTMLDivElement>(null);
    const [scrolledPastHero, setScrolledPastHero] = useState(false);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        const heroEl = heroRef.current;
        if (!el || !heroEl) return;
        setScrolledPastHero(el.scrollTop > heroEl.offsetHeight - 60);
        // Auto-hide explainer on first scroll
        if (el.scrollTop > 48) setShowExplainer(false);
    }, []);

    const iconBoxSx = {
        width: 32,
        height: 32,
        borderRadius: "10px",
        bgcolor: alpha(primary, 0.1),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
    };

    useEffect(() => {
        if (!uuid || !brand_id) {
            navigate("/");
            return;
        }

        Location.list(uuid, brand_id).then((res) => {
            if (res) setLocationId(res[0].key);
        });

        if (locationId) {
            Location.info(uuid, brand_id, locationId)
                .then((res) => {
                    if (!res) return;
                    const location = res;

                    setLocation(location);
                    setName(location.name);
                    setCity(location.city);
                    setAddress(location.address);
                    setTimeZone(location.timezone);
                    setCurrency(location.currency);
                    setMonthlyRevenueGoal(
                        location.monthlyRevenueGoal != null
                            ? String(location.monthlyRevenueGoal)
                            : ""
                    );
                    setMonthlyHoursGoal(
                        location.monthlyHoursGoal != null
                            ? String(location.monthlyHoursGoal)
                            : ""
                    );
                    setWeek({
                        MO: {
                            enabled: !!location.mo,
                            start: location.moStart,
                            end: location.moEnd,
                        },
                        TU: {
                            enabled: !!location.tu,
                            start: location.tuStart,
                            end: location.tuEnd,
                        },
                        WE: {
                            enabled: !!location.we,
                            start: location.weStart,
                            end: location.weEnd,
                        },
                        TH: {
                            enabled: !!location.th,
                            start: location.thStart,
                            end: location.thEnd,
                        },
                        FR: {
                            enabled: !!location.fr,
                            start: location.frStart,
                            end: location.frEnd,
                        },
                        SA: {
                            enabled: !!location.sa,
                            start: location.saStart,
                            end: location.saEnd,
                        },
                        SU: {
                            enabled: !!location.su,
                            start: location.suStart,
                            end: location.suEnd,
                        },
                    });
                })
                .catch(console.error);
        }

        Brand.info(uuid, brand_id).then((b) => {
            setLocationConfigured(!!b?.setupSteps?.location);
        });
    }, [uuid, brand_id, locationId, navigate]);

    // Live validation hook
    useEffect(() => {
        const bad = DAY_ORDER.some(
            (d) => week[d].enabled && week[d].end <= week[d].start
        );
        setHasValidationError(bad);
    }, [week]);

    useEffect(() => {
        if (locationConfigured === false) setShowExplainer(true);
    }, [locationConfigured]);

    const onExitOrSave = useCallback(async () => {
        if (!uuid || !brand_id || isSaving) return;
        if (hasValidationError) return;

        setIsSaving(true);
        try {
            if (!location) throw new Error("No location to update");

            await Location.update(
                uuid,
                brand_id,
                location?.key ?? "",
                name,
                city,
                address,
                timeZone,
                currency,
                // MO
                week.MO.enabled,
                week.MO.start,
                week.MO.end,
                // TU
                week.TU.enabled,
                week.TU.start,
                week.TU.end,
                // WE
                week.WE.enabled,
                week.WE.start,
                week.WE.end,
                // TH
                week.TH.enabled,
                week.TH.start,
                week.TH.end,
                // FR
                week.FR.enabled,
                week.FR.start,
                week.FR.end,
                // SA
                week.SA.enabled,
                week.SA.start,
                week.SA.end,
                // SU
                week.SU.enabled,
                week.SU.start,
                week.SU.end,
                getPreferredLanguage(),
                undefined,
                locationConfigured ? undefined : true,
                monthlyRevenueGoal.trim() === ""
                    ? null
                    : Number(monthlyRevenueGoal),
                monthlyHoursGoal.trim() === ""
                    ? null
                    : Number(monthlyHoursGoal)
            );
            navigate("/");
        } catch (err) {
            alert("Error updating the location");
            setIsSaving(false);
        }
    }, [
        uuid,
        brand_id,
        location,
        name,
        city,
        address,
        timeZone,
        currency,
        week,
        monthlyRevenueGoal,
        monthlyHoursGoal,
        hasValidationError,
        navigate,
        locationConfigured,
        isSaving,
    ]);

    const renderDayRow = (d: DayCode) => {
        const day = week[d];
        const setDay = (patch: Partial<DayCfg>) =>
            setWeek((w) => ({ ...w, [d]: { ...w[d], ...patch } }));

        return (
            <Stack
                key={d}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
            >
                {/* Day badge */}
                <Box
                    className="force-dynamic-translation"
                    sx={{
                        height: 28,
                        px: 0.75,
                        borderRadius: "8px",
                        bgcolor: alpha(primary, day.enabled ? 0.1 : 0.04),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: "0 0 auto",
                        minWidth: 36,
                        transition: "background-color 0.2s ease",
                    }}
                >
                    <Typography
                        fontSize="11px"
                        fontWeight={600}
                        color={day.enabled ? primary : "text.disabled"}
                        sx={{ transition: "color 0.2s ease" }}
                    >
                        {DAY_LABELS[d]}
                    </Typography>
                </Box>

                {/* Time selects */}
                <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    sx={{
                        flex: 1,
                        justifyContent: "center",
                        opacity: day.enabled ? 1 : 0.35,
                        transition: "opacity 0.2s ease",
                    }}
                >
                    <Select
                        variant="standard"
                        value={day.start}
                        onChange={(e) =>
                            setDay({ start: Number(e.target.value) })
                        }
                        disabled={!day.enabled}
                        disableUnderline
                        sx={{
                            color: primary,
                            fontWeight: 600,
                            fontSize: "14px",
                            minWidth: 58,
                            "& .MuiSelect-select": { py: 0.25 },
                        }}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
                    >
                        {ensureSlot(day.start, TIME_SLOTS).map((t) => (
                            <MenuItem key={t} value={t}>
                                {toHHMM(t)}
                            </MenuItem>
                        ))}
                    </Select>
                    <Typography
                        fontSize="12px"
                        color="text.secondary"
                    >
                        –
                    </Typography>
                    <Select
                        variant="standard"
                        value={day.end}
                        onChange={(e) =>
                            setDay({ end: Number(e.target.value) })
                        }
                        disabled={!day.enabled}
                        disableUnderline
                        sx={{
                            color: primary,
                            fontWeight: 600,
                            fontSize: "14px",
                            minWidth: 58,
                            "& .MuiSelect-select": { py: 0.25 },
                        }}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
                    >
                        {ensureSlot(day.end, TIME_SLOTS).map((t) => (
                            <MenuItem key={t} value={t}>
                                {toHHMM(t)}
                            </MenuItem>
                        ))}
                    </Select>
                </Stack>

                {/* Toggle */}
                <ToggleButton
                    value={day.enabled}
                    onClick={() => setDay({ enabled: !day.enabled })}
                />
            </Stack>
        );
    };

    return (
        <>
            <Stack
                ref={scrollRef}
                onScroll={handleScroll}
                flex={1}
                sx={{ overflowY: "auto", overflowX: "hidden", bgcolor: "white" }}
            >
                {/* ── Hero (compact) ── */}
                <Stack
                    ref={heroRef}
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        width: "100%",
                        minHeight: 120,
                        position: "relative",
                        zIndex: 0,
                        background: `linear-gradient(180deg, ${lighten(primary, 0.08)} 0%, ${darken(primary, 0.08)} 100%)`,
                        transition: "background 0.3s ease",
                        pt: "40px",
                        pb: 4,
                        px: 2,
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            opacity: 0.04,
                            backgroundImage: NOISE_TEXTURE,
                            backgroundRepeat: "repeat",
                            backgroundSize: "128px 128px",
                            pointerEvents: "none",
                        },
                    }}
                >
                    {/* Back arrow */}
                    <ButtonBase
                        onClick={onExitOrSave}
                        disabled={isSaving}
                        sx={{
                            position: "absolute",
                            top: 12,
                            left: 10,
                            zIndex: 2,
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            bgcolor: "rgba(60,60,60,0.35)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {isSaving ? (
                            <CircularProgress size={18} sx={{ color: "white" }} />
                        ) : (
                            <ColoredIcon icon={BackIcon} color="white" size="28px" />
                        )}
                    </ButtonBase>

                    {/* Frosted circle + icon */}
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            bgcolor: "rgba(60,60,60,0.3)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.15)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 1,
                        }}
                    >
                        <StorefrontRounded sx={{ fontSize: 32, color: "white" }} />
                    </Box>

                    {/* Frosted pill */}
                    <Box
                        sx={{
                            bgcolor: "rgba(60,60,60,0.35)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            borderRadius: "16px",
                            px: 1.5,
                            py: 0.5,
                        }}
                    >
                        <Typography
                            fontSize="12px"
                            fontWeight={500}
                            color="white"
                        >
                            Schedule & Location
                        </Typography>
                    </Box>
                </Stack>

                {/* ── Sticky Toolbar ── */}
                <Box
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 4,
                        bgcolor: "white",
                        borderTopLeftRadius: "24px",
                        borderTopRightRadius: "24px",
                        mt: "-24px",
                        pt: 1.5,
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                            maxHeight: scrolledPastHero ? 52 : 0,
                            opacity: scrolledPastHero ? 1 : 0,
                            overflow: "hidden",
                            transition:
                                "max-height 0.25s ease, opacity 0.2s ease",
                            px: 1,
                            borderBottom: scrolledPastHero
                                ? "1px solid rgba(0,0,0,0.06)"
                                : "none",
                        }}
                    >
                        <ButtonBase
                            onClick={onExitOrSave}
                            disabled={isSaving}
                            sx={{ p: 0.5 }}
                        >
                            {isSaving ? (
                                <CircularProgress size={18} sx={{ color: primary }} />
                            ) : (
                                <ColoredIcon
                                    icon={BackIcon}
                                    color={primary}
                                    size="28px"
                                />
                            )}
                        </ButtonBase>
                        <Typography
                            fontFamily={FONTS.display}
                            fontSize="16px"
                            fontWeight={600}
                            ml={0.5}
                            noWrap
                            sx={{
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {name || "Schedule & Location"}
                        </Typography>
                    </Stack>
                </Box>

                {/* ── Bottom Sheet Content ── */}
                <Stack
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        bgcolor: "white",
                        boxSizing: "border-box",
                        px: 2,
                        pt: 0.5,
                        pb: "calc(4vh + 90px)",
                        "@keyframes fadeSlideIn": {
                            from: {
                                opacity: 0,
                                transform: "translateY(12px)",
                            },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                        animation: "fadeSlideIn 0.3s ease-out",
                    }}
                    spacing={2}
                >
                    {/* Inline explainer for first-time setup */}
                    {showExplainer && (
                        <InlineExplainer
                            text="Here you can set your workspace's name, location, and working hours. This is the time available for the bookings in the app."
                            onClose={() => setShowExplainer(false)}
                        />
                    )}

                    {/* ── LOCATION section ── */}
                    <Typography
                        fontSize="11px"
                        fontWeight={600}
                        color="text.secondary"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                        mb={-1}
                    >
                        Location
                    </Typography>
                    <Stack sx={CARD_STYLES} spacing={2.5}>
                        <Stack spacing={0.75}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                            >
                                <Box sx={iconBoxSx}>
                                    <StorefrontRounded
                                        sx={{ fontSize: 18, color: primary }}
                                    />
                                </Box>
                                <Typography sx={FORM_LABEL_SX}>
                                    Salon / Studio
                                </Typography>
                            </Stack>
                            <TextField
                                value={name}
                                onChange={(e) => setName(e.currentTarget.value)}
                                variant="underlined"
                                nextRef={cityInputRef}
                            />
                        </Stack>
                        <Stack spacing={0.75}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                            >
                                <Box sx={iconBoxSx}>
                                    <LocationCityRounded
                                        sx={{ fontSize: 18, color: primary }}
                                    />
                                </Box>
                                <Typography sx={FORM_LABEL_SX}>
                                    City / Town
                                </Typography>
                            </Stack>
                            <TextField
                                value={city}
                                onChange={(e) => setCity(e.currentTarget.value)}
                                variant="underlined"
                                ref={cityInputRef}
                                nextRef={addressInputRef}
                            />
                        </Stack>
                        <Stack spacing={0.75}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                            >
                                <Box sx={iconBoxSx}>
                                    <PlaceRounded
                                        sx={{ fontSize: 18, color: primary }}
                                    />
                                </Box>
                                <Typography sx={FORM_LABEL_SX}>
                                    Address
                                </Typography>
                            </Stack>
                            <TextField
                                value={address}
                                onChange={(e) =>
                                    setAddress(e.currentTarget.value)
                                }
                                variant="underlined"
                                ref={addressInputRef}
                            />
                        </Stack>
                    </Stack>

                    {/* ── SETTINGS section ── */}
                    <Typography
                        fontSize="11px"
                        fontWeight={600}
                        color="text.secondary"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                        mb={-1}
                    >
                        Settings
                    </Typography>
                    <Stack sx={CARD_STYLES} spacing={2.5}>
                        <Stack spacing={0.75}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                            >
                                <Box sx={iconBoxSx}>
                                    <LanguageRounded
                                        sx={{ fontSize: 18, color: primary }}
                                    />
                                </Box>
                                <Typography sx={FORM_LABEL_SX}>
                                    Time Zone
                                </Typography>
                            </Stack>
                            <Select
                                variant="standard"
                                value={timeZone}
                                onChange={(e) => setTimeZone(e.target.value)}
                                sx={{
                                    color: primary,
                                    fontWeight: 600,
                                    fontSize: "14px",
                                }}
                                disableUnderline
                            >
                                <MenuItem value="Europe/Berlin">
                                    Europe / Madrid, Warsaw
                                </MenuItem>
                                <MenuItem value="Europe/Kyiv">
                                    Europe / Kyiv
                                </MenuItem>
                            </Select>
                        </Stack>
                        <Stack spacing={0.75}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                            >
                                <Box sx={iconBoxSx}>
                                    <AttachMoneyRounded
                                        sx={{ fontSize: 18, color: primary }}
                                    />
                                </Box>
                                <Typography sx={FORM_LABEL_SX}>
                                    Currency
                                </Typography>
                            </Stack>
                            <Select
                                variant="standard"
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value)}
                                sx={{
                                    color: primary,
                                    fontWeight: 600,
                                    fontSize: "14px",
                                }}
                                disableUnderline
                            >
                                <MenuItem value="EUR">EUR</MenuItem>
                                <MenuItem value="USD">USD</MenuItem>
                                <MenuItem value="UAH">UAH</MenuItem>
                                <MenuItem value="PLN">PLN</MenuItem>
                                <MenuItem value="KWD">KWD</MenuItem>
                            </Select>
                        </Stack>
                    </Stack>

                    {/* ── MONTHLY GOALS section ── */}
                    <Typography
                        fontSize="11px"
                        fontWeight={600}
                        color="text.secondary"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                        mb={-1}
                    >
                        Monthly Goals
                    </Typography>
                    <Stack sx={{ ...CARD_STYLES, py: 1.5 }} spacing={1.5}>
                        <Stack spacing={0.5}>
                            <Typography sx={FORM_LABEL_SX}>
                                Monthly revenue goal (€)
                            </Typography>
                            <TextField
                                type="number"
                                value={monthlyRevenueGoal}
                                onChange={(e) =>
                                    setMonthlyRevenueGoal(e.target.value)
                                }
                                placeholder="e.g. 3000"
                                fullWidth
                            />
                        </Stack>
                        <Stack spacing={0.5}>
                            <Typography sx={FORM_LABEL_SX}>
                                Desired working hours / month (optional)
                            </Typography>
                            <TextField
                                type="number"
                                value={monthlyHoursGoal}
                                onChange={(e) =>
                                    setMonthlyHoursGoal(e.target.value)
                                }
                                placeholder="e.g. 120"
                                fullWidth
                            />
                        </Stack>
                    </Stack>

                    {/* ── WORKING HOURS section ── */}
                    <Typography
                        fontSize="11px"
                        fontWeight={600}
                        color="text.secondary"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                        mb={-1}
                    >
                        Working Hours
                    </Typography>
                    <Stack sx={{ ...CARD_STYLES, py: 1.5 }} spacing={1}>
                        {/* Weekdays */}
                        {(["MO", "TU", "WE", "TH", "FR"] as DayCode[]).map(
                            renderDayRow
                        )}

                        {/* Weekend separator */}
                        <Box
                            sx={{
                                borderTop: `1px solid ${alpha(primary, 0.08)}`,
                                mx: -2,
                            }}
                        />

                        {/* Weekend */}
                        {(["SA", "SU"] as DayCode[]).map(renderDayRow)}
                    </Stack>

                    {hasValidationError && (
                        <Typography color="error.main" fontSize="13px">
                            Each enabled day must have end time after start time.
                        </Typography>
                    )}
                </Stack>
            </Stack>

            <BottomButton
                onClick={onExitOrSave}
                sticky
                disabled={hasValidationError || isSaving}
            >
                Save Changes
            </BottomButton>
        </>
    );
};

export default WorkspaceEdit;
